import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import {
    Package, Plus, Search, FileSpreadsheet, Monitor, Laptop, Smartphone,
    Tablet, Printer, Key, AppWindow, Headphones, Wifi, CreditCard, Settings, QrCode, Copy, Users, Plug, Usb, Check,
} from "lucide-react";
import { EquipmentForm, emptyEquipmentForm, buildEquipmentPayload, itemToForm } from "../inventory/EquipmentForm";
import { HandoverSection, emptyHandover, ConditionBadge, CONDITION_LABELS } from "../inventory/HandoverSection";
import { RebillBadge, REBILL_OPTIONS, rebillStatus } from "../inventory/RebillFields";
import { formatDate } from "../../lib/utils";
import { ReturnDialog } from "../inventory/ReturnDialog";
import { ItemHoldersDialog } from "../inventory/ItemHoldersDialog";
import { ItemSignatureBadge } from "../inventory/SignatureBadges";
import { QrLabelDialog } from "../inventory/QrLabelDialog";
import { CategorySettingsDialog } from "../inventory/CategorySettingsDialog";
import { getCategoryIcon, loadInventoryCategories } from "../inventory/categoryIcons";

const STATUS_CONFIG = {
    in_stock: { label: "En stock", color: "bg-emerald-100 text-emerald-700" },
    assigned: { label: "Assigné", color: "bg-blue-100 text-blue-700" },
    maintenance: { label: "Maintenance", color: "bg-orange-100 text-orange-700" },
    retired: { label: "Retiré", color: "bg-gray-200 text-gray-600" },
    lost: { label: "Perdu", color: "bg-red-100 text-red-700" },
    broken: { label: "Endommagé", color: "bg-red-100 text-red-600" },
};

export default function InventoryItemsView() {
    const navigate = useNavigate();
    const { users, departments, currentUser, hasPermission } = useApp();
    const canManage = hasPermission("inventory:manage");
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState(null);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [catFilter, setCatFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [labeledFilter, setLabeledFilter] = useState("");
    const [conditionFilter, setConditionFilter] = useState("");
    const [rebillFilter, setRebillFilter] = useState("");
    const [search, setSearch] = useState("");
    const [createDialog, setCreateDialog] = useState(false);
    const [assignDialog, setAssignDialog] = useState(null);
    const [assignTarget, setAssignTarget] = useState(null);
    const [handover, setHandover] = useState(emptyHandover(null));
    const [photos, setPhotos] = useState([]);
    const [returnItem, setReturnItem] = useState(null);
    const [holdersItem, setHoldersItem] = useState(null);
    const [createForm, setCreateForm] = useState(emptyEquipmentForm);
    const [qrItem, setQrItem] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const loadData = useCallback(async () => {
        try {
            const params = {};
            if (catFilter) params.category = catFilter;
            if (statusFilter) params.status = statusFilter;
            if (labeledFilter !== "") params.labeled = labeledFilter;
            if (conditionFilter) params.condition = conditionFilter;
            if (rebillFilter) params.rebill = rebillFilter;
            if (search.trim()) params.search = search.trim();
            const [result, cats, st] = await Promise.all([
                api.getInventoryItems(params),
                loadInventoryCategories(true),
                api.getInventoryStats(),
            ]);
            setItems(result.items);
            setCategories(cats);
            setStats(st);
        } catch (e) {
            toast.error("Erreur de chargement");
        } finally { setLoading(false); }
    }, [catFilter, statusFilter, labeledFilter, conditionFilter, rebillFilter, search]);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { api.getInventoryEmployees().then((r) => setPeople(r.employees)).catch(() => {}); }, []);

    const userMap = React.useMemo(() => {
        const m = {};
        users.forEach((u) => { m[u.id] = `${u.first_name} ${u.last_name}`; });
        people.forEach((p) => { m[p.id] = `${p.first_name} ${p.last_name}`; });
        return m;
    }, [users, people]);

    const peopleList = React.useMemo(
        () => people.map((p) => ({ id: p.id, first_name: p.first_name, last_name: p.last_name, email: p.email || "", department_id: p.department_id })),
        [people],
    );

    const handleCreate = async () => {
        try {
            const item = await api.createInventoryItem(buildEquipmentPayload(createForm));
            toast.success(`"${item.asset_tag || item.name}" créé`);
            setCreateDialog(false);
            setCreateForm(emptyEquipmentForm);
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const openAssign = (item) => {
        setAssignDialog(item);
        setAssignTarget(null);
        setHandover(emptyHandover(currentUser?.id));
        setPhotos([]);
    };

    const handleAssign = async () => {
        if (!assignDialog || !assignTarget) return;
        try {
            const res = await api.assignInventoryItem(assignDialog.id, {
                user_id: assignTarget,
                condition: handover.condition,
                condition_notes: handover.condition_notes,
                handover_date: handover.date_undefined ? null : handover.date,
                handover_date_undefined: !!handover.date_undefined,
                handed_over_by: handover.handed_over_by,
            });
            for (const f of photos) {
                await api.uploadInventoryAttachment(assignDialog.id, f, `Photo remise ${handover.date || ""}`.trim(), { category: "etat_des_lieux", historyId: res.history_id });
            }
            toast.success(`"${assignDialog.asset_tag || assignDialog.name}" assigné`);
            setAssignDialog(null);
            setAssignTarget(null);
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const toggleLabeled = async (item) => {
        try {
            await api.updateInventoryItem(item.id, { labeled: !item.labeled });
            toast.success(!item.labeled ? "Marqué comme étiqueté" : "Marqué comme non étiqueté");
            loadData();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {};
            if (catFilter) params.category = catFilter;
            if (statusFilter) params.status = statusFilter;
            if (conditionFilter) params.condition = conditionFilter;
            if (rebillFilter) params.rebill = rebillFilter;
            if (labeledFilter !== "") params.labeled = labeledFilter;
            if (search.trim()) params.search = search.trim();
            const response = await api.exportInventoryExcel(params);
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `inventaire_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch { toast.error("Erreur export"); }
        finally { setExporting(false); }
    };

    // Clic molette / Ctrl+clic → nouvel onglet ; clic simple → navigation
    const openPath = (e, path) => {
        if (e.ctrlKey || e.metaKey || e.button === 1) window.open(path, "_blank", "noopener");
        else navigate(path);
    };
    const linkProps = (path, stop = false) => ({
        onClick: (e) => { if (stop) e.stopPropagation(); openPath(e, path); },
        onAuxClick: (e) => { if (e.button === 1) { e.preventDefault(); if (stop) e.stopPropagation(); openPath(e, path); } },
        onMouseDown: (e) => { if (e.button === 1) e.preventDefault(); },
    });

    const assignedDate = (item) => {
        if (item.status !== "assigned") return null;
        if (item.handover_date_undefined) return <span className="text-gray-400 italic">Indéfinie</span>;
        const d = item.handover_date || item.assigned_at;
        return d ? formatDate(d, { month: "2-digit", day: "2-digit" }) : null;
    };

    const getWarrantyBadge = (item) => {
        if (!item.warranty_end) return null;
        const end = new Date(item.warranty_end);
        const now = new Date();
        const diff = (end - now) / (1000 * 60 * 60 * 24);
        if (diff < 0) return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Expirée</span>;
        if (diff < 30) return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Bientôt</span>;
        return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">OK</span>;
    };

    if (loading) return <div className="animate-pulse text-gray-400 py-8">Chargement de l'inventaire...</div>;

    return (
        <div data-testid="inventory-items-view">
            {/* Toolbar */}
            <div className="flex items-center justify-end gap-2 mb-4 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="inventory-settings-btn">
                    <Settings className="w-4 h-4 mr-1" />Paramètres
                </Button>
                <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-inventory-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-1" />{exporting ? "..." : "Exporter"}
                </Button>
                {canManage && <Button onClick={() => { setCreateForm(emptyEquipmentForm); setCreateDialog(true); }} data-testid="create-item-btn">
                    <Plus className="w-4 h-4 mr-1" />Nouvel item
                </Button>}
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="inventory-stats">
                    <div className="px-4 py-3 rounded-lg bg-gray-50 text-center"><p className="text-xl font-bold text-gray-800">{stats.total}</p><p className="text-[10px] text-gray-500">Total items</p></div>
                    <div className="px-4 py-3 rounded-lg bg-blue-50 text-center"><p className="text-xl font-bold text-blue-700">{stats.by_status?.assigned || 0}</p><p className="text-[10px] text-blue-500">Assignés</p></div>
                    <div className="px-4 py-3 rounded-lg bg-emerald-50 text-center"><p className="text-xl font-bold text-emerald-700">{stats.by_status?.in_stock || 0}</p><p className="text-[10px] text-emerald-500">En stock</p></div>
                    <div className="px-4 py-3 rounded-lg bg-orange-50 text-center"><p className="text-xl font-bold text-orange-700">{stats.by_status?.maintenance || 0}</p><p className="text-[10px] text-orange-500">Maintenance</p></div>
                    <button type="button" onClick={() => setRebillFilter(rebillFilter === "pending" ? "" : "pending")} className={`px-4 py-3 rounded-lg bg-amber-50 text-center border transition-colors ${rebillFilter === "pending" ? "border-amber-400" : "border-transparent hover:border-amber-200"}`} title="Afficher le matériel restant à refacturer" data-testid="stat-rebill-pending">
                        <p className="text-xl font-bold text-amber-700">{stats.rebill_pending || 0}</p><p className="text-[10px] text-amber-600">À refacturer</p>
                    </button>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Asset Tag, nom, N° série..." className="pl-9" data-testid="inventory-search" />
                </div>
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="inventory-cat-filter">
                    <option value="">Toutes catégories</option>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="inventory-status-filter">
                    <option value="">Tous statuts</option>
                    <option value="in_stock">En stock</option>
                    <option value="assigned">Assignés</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retirés</option>
                    <option value="lost">Perdus</option>
                    <option value="broken">Endommagés</option>
                </select>
                <select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="inventory-condition-filter">
                    <option value="">Tous états</option>
                    {Object.entries(CONDITION_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    <option value="none">Sans état</option>
                </select>
                <select value={rebillFilter} onChange={(e) => setRebillFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="inventory-rebill-filter">
                    <option value="">Refacturation : toutes</option>
                    {REBILL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={labeledFilter} onChange={(e) => setLabeledFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="inventory-labeled-filter">
                    <option value="">Étiquette : tous</option>
                    <option value="1">Étiquetés</option>
                    <option value="0">Non étiquetés</option>
                </select>
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-body p-0 overflow-x-auto">
                    {items.length === 0 ? (
                        <p className="text-sm text-gray-400 py-8 text-center">Aucun item dans l'inventaire</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="border-b border-gray-100 text-left">
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap">Asset Tag</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Nom</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Catégorie</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">N° série</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Assigné à</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap">Assigné le</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Statut</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">État</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Refacturation</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Étiquette</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500">Garantie</th>
                                <th className="px-3 py-2.5 text-xs font-medium text-gray-500 text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {items.map((item) => {
                                    const CatIcon = getCategoryIcon(categories, item.category);
                                    const catLabel = categories.find((c) => c.key === item.category)?.label || item.category;
                                    const sCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.in_stock;
                                    return (
                                        <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" {...linkProps(`/admin/inventory/${item.id}`)} title="Clic molette ou Ctrl+clic : ouvrir dans un nouvel onglet" data-testid={`inv-row-${item.id}`}>
                                            <td className="px-3 py-2.5 text-xs text-gray-900 font-mono font-semibold">{item.asset_tag || "—"}</td>
                                            <td className="px-3 py-2.5 text-gray-700">{item.name || [item.brand, item.model].filter(Boolean).join(" ") || "—"}</td>
                                            <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1 text-xs text-gray-500"><CatIcon className="w-3 h-3" />{catLabel}</span></td>
                                            <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{item.serial_number || "—"}</td>
                                            <td className="px-3 py-2.5 text-xs">{item.assigned_to_user_id ? (
                                                <div>
                                                    <button className="text-blue-600 hover:underline" {...linkProps(`/admin/inventory/employee/${item.assigned_to_user_id}`, true)}>{userMap[item.assigned_to_user_id] || item.last_holder_name || "—"}</button>
                                                    <div className="mt-0.5"><ItemSignatureBadge item={item} compact testId={`item-sig-${item.id}`} /></div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="text-gray-400">En stock</span>
                                                    {item.last_holder_id && item.last_holder_name && (
                                                        <button
                                                            className="block text-[10px] text-gray-400 hover:text-teal-600 hover:underline"
                                                            {...linkProps(`/admin/inventory/employee/${item.last_holder_id}`, true)}
                                                            title="Dernière personne qui avait ce matériel"
                                                            data-testid={`last-holder-${item.id}`}
                                                        >
                                                            Dernier : {item.last_holder_name}
                                                        </button>
                                                    )}
                                                </div>
                                            )}</td>
                                            <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap" data-testid={`assigned-date-${item.id}`}>{assignedDate(item) || <span className="text-gray-300">—</span>}</td>
                                            <td className="px-3 py-2.5"><span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${sCfg.color}`}>{sCfg.label}</span></td>
                                            <td className="px-3 py-2.5">
                                                {(item.condition || item.handover_condition)
                                                    ? <ConditionBadge condition={item.condition || item.handover_condition} testId={`condition-${item.id}`} />
                                                    : <span className="text-gray-300 text-xs">—</span>}
                                            </td>
                                            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-1.5">
                                                    <RebillBadge item={item} testId={`rebill-${item.id}`} compact />
                                                    {canManage && rebillStatus(item) === "pending" && (
                                                        <button type="button" onClick={async (e) => { e.stopPropagation(); try { await api.confirmInventoryRebill(item.id); toast.success("Refacturation validée"); loadData(); } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } }}
                                                            className="inline-flex items-center justify-center w-6 h-6 rounded-md border border-emerald-200 text-emerald-600 hover:bg-emerald-50" title="Valider : refacturation effectuée" data-testid={`rebill-confirm-${item.id}`}>
                                                            <Check className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                                <button onClick={() => toggleLabeled(item)} title="Cliquer pour changer" data-testid={`labeled-toggle-${item.id}`}
                                                    className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap transition-colors ${item.labeled ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 border border-dashed border-gray-300 hover:bg-gray-200"}`}>
                                                    <QrCode className="w-2.5 h-2.5" />{item.labeled ? "Étiqueté" : "Non étiqueté"}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2.5">{getWarrantyBadge(item) || "—"}</td>
                                            <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <Button variant="ghost" size="sm" onClick={() => setHoldersItem(item)} title="Historique des détenteurs" data-testid={`holders-${item.id}`}><Users className="w-3.5 h-3.5" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => setQrItem(item)} title="Étiquette QR" data-testid={`qr-${item.id}`}><QrCode className="w-3.5 h-3.5" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => { setCreateForm(itemToForm(item)); setCreateDialog(true); }} title="Dupliquer (utiliser comme modèle)" data-testid={`duplicate-${item.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                                                {item.status === "in_stock" && <Button variant="ghost" size="sm" onClick={() => openAssign(item)} data-testid={`assign-${item.id}`}>Assigner</Button>}
                                                {item.status === "assigned" && <Button variant="ghost" size="sm" onClick={() => setReturnItem(item)} data-testid={`unassign-${item.id}`}>Restituer</Button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Create Dialog */}
            <Dialog open={createDialog} onOpenChange={setCreateDialog}>
                <DialogContent className="max-w-xl" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <DialogHeader><DialogTitle>Nouvel item d'inventaire</DialogTitle></DialogHeader>
                    <div className="py-2 max-h-[60vh] overflow-y-auto pr-1">
                        <EquipmentForm form={createForm} setForm={setCreateForm} categories={categories} />
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button><Button onClick={handleCreate} data-testid="confirm-create-inv-btn">Créer</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Dialog */}
            <Dialog open={!!assignDialog} onOpenChange={(o) => { if (!o) setAssignDialog(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Assigner "{assignDialog?.asset_tag || assignDialog?.name}"</DialogTitle></DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto py-1 pr-1">
                        <div><label className="form-label text-xs">Employé</label>
                            <UserSearchSelect users={peopleList} departments={departments} value={assignTarget} onChange={setAssignTarget} placeholder="Sélectionner..." />
                        </div>
                        <HandoverSection value={handover} onChange={setHandover} photos={photos} setPhotos={setPhotos} people={peopleList} departments={departments} mode="remise" />
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setAssignDialog(null)}>Annuler</Button><Button onClick={handleAssign} disabled={!assignTarget} data-testid="confirm-assign-inv-btn">Assigner</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Return Dialog */}
            <ReturnDialog item={returnItem} onClose={() => setReturnItem(null)} onDone={loadData} people={peopleList} departments={departments} />

            {/* Holders history Dialog */}
            <ItemHoldersDialog item={holdersItem} onClose={() => setHoldersItem(null)} />

            {/* QR Label Dialog */}
            <QrLabelDialog item={qrItem} open={!!qrItem} onClose={() => setQrItem(null)} onLabeled={loadData} />

            <CategorySettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} categories={categories} onChanged={async () => { await loadInventoryCategories(true); loadData(); }} />
        </div>
    );
}
